import { Injectable, type Signal, computed, inject } from "@angular/core";
import { WkDataTableStoreService } from "@components/data-table/store";
import type {
    WkDataTableInternalData,
    WkDataTableSorting,
    WkDataTableSortItems,
    WkDataTableBaseSortItem,
} from "@components/data-table/data-table.types";
import {
    DEFAULT_SORTING,
    WkDataTableSortDirectionEnum,
    WkDataTableFieldTypeEnum,
    WkDataTableInternalDataItem,
} from "@components/data-table/data-table.types";
import { WkDataTableContextService } from "@components/data-table/data-table-context.service";
import { computedEqual } from "@shared/utils";

@Injectable()
export class WkDataTableSortService<TItem> {
    private readonly wkDataTableStore = inject(WkDataTableStoreService);
    private readonly wkDataTableContext = inject<WkDataTableContextService<TItem>>(WkDataTableContextService);

    public readonly columns = computed(() => {
        return this.wkDataTableStore
            .visibleColumns()
            .filter((c) => c.definition.sortable ?? false)
            .sort((c1, c2) => c1.sortOrder() - c2.sortOrder());
    });

    public readonly items = computed(() => {
        return new Map(
            this.columns()
                .filter((c) => c.isSorted())
                .map((c) => [
                    c.fullFieldName(),
                    {
                        fieldname: c.fullFieldName(),
                        direction: c.sortDirection(),
                        order: c.sortOrder(),
                        type: c.definition.type,
                    },
                ]),
        ) as WkDataTableSortItems;
    });
    public sorting = computed<WkDataTableSorting>(
        () => {
            const _sorting = this.wkDataTableContext.sorting();
            const items = this.items();

            return {
                ...DEFAULT_SORTING,
                ..._sorting,
                items,
            };
        },
        { equal: computedEqual },
    );

    public applySort(data: Signal<WkDataTableInternalData<TItem>>): Signal<WkDataTableInternalData<TItem>> {
        return computed(() => {
            const items = data();
            const sorting = this.sorting();
            if (!items.length || !sorting?.items?.size) return items;

            return this.sortByFieldsAdvanced(items, sorting.items);
        });
    }

    private sortByFieldsAdvanced(items: WkDataTableInternalData<TItem>, sorting: WkDataTableSortItems): WkDataTableInternalData<TItem> {
        const sortingArray = Array.from(sorting?.values?.() ?? []).sort((s1, s2) => s1.order - s2.order);
        return items.toSorted((a, b) => {
            for (const sort of sortingArray) {
                const result = this.sortByField(a, b, sort);
                if (result !== 0) return result;
            }

            return 0;
        });
    }

    private sortByField(a: WkDataTableInternalDataItem<TItem>, b: WkDataTableInternalDataItem<TItem>, sort: WkDataTableBaseSortItem): number {
        const field = sort.fieldname;
        const column = this.wkDataTableStore.get(field);
        const direction = sort.direction;
        const type = sort.type ?? column?.definition.type;

        const va = column?.getSortValue()?.(a.value);
        const vb = column?.getSortValue()?.(b.value);

        const missingValueOrder = this.compareMissingValues(va, vb, direction);
        if (missingValueOrder !== 0) return missingValueOrder;

        const result = va != null && vb != null ? this.compareByType(va, vb, type) : 0;

        if (result !== 0) {
            return direction === WkDataTableSortDirectionEnum.Asc ? result : -result;
        }

        for (const fallback of column?.definition.sortFallback ?? []) {
            const fallbackResult = this.sortByField(a, b, {
                fieldname: fallback.fieldname,
                direction: fallback.direction,
            });

            if (fallbackResult !== 0) return fallbackResult;
        }
        return 0;
    }

    private compareMissingValues(va: unknown, vb: unknown, direction: WkDataTableSortDirectionEnum): number {
        if (va == null && vb != null) return direction === WkDataTableSortDirectionEnum.Desc ? -1 : 1;
        if (va != null && vb == null) return direction === WkDataTableSortDirectionEnum.Asc ? 1 : -1;
        return 0;
    }

    private compareByType(va: unknown, vb: unknown, type: WkDataTableFieldTypeEnum | undefined): number {
        switch (type) {
            case WkDataTableFieldTypeEnum.Number:
                return Number(va) - Number(vb);

            case WkDataTableFieldTypeEnum.Datetime:
                return new Date(va as any).getTime() - new Date(vb as any).getTime();

            case WkDataTableFieldTypeEnum.String:
            default:
                return String(va).localeCompare(String(vb));
        }
    }
}
